/**
 * useActionHighlight Hook
 *
 * Manages state for highlighting DOM elements during UI action execution.
 *
 * Features:
 * - Highlight single elements
 * - Find and highlight elements by CSS selector
 * - Cycle through multiple elements
 * - Configurable animation and padding
 */

import { useCallback, useRef, useState } from 'react';

export interface UseActionHighlightOptions {
  /** Whether to animate the highlight (default: true) */
  animate?: boolean;
  /** Padding around the highlighted element (default: 8) */
  padding?: number;
}

export interface UseActionHighlightReturn {
  /** The currently highlighted element */
  targetElement: HTMLElement | null;
  /** Label to display with the highlight */
  label: string | undefined;
  /** Whether an element is currently highlighted */
  isHighlighting: boolean;
  /** Whether to animate the highlight */
  animate: boolean;
  /** Padding around the element */
  padding: number;
  /** Highlight a specific element */
  highlight: (element: HTMLElement, label?: string) => void;
  /** Find and highlight an element by selector */
  highlightBySelector: (selector: string, label?: string) => boolean;
  /** Clear the current highlight */
  clear: () => void;
  /** Highlight multiple elements in sequence */
  highlightMultiple: (elements: HTMLElement[], options?: { interval?: number; labels?: string[] }) => void;
}

export function useActionHighlight(options: UseActionHighlightOptions = {}): UseActionHighlightReturn {
  const { animate = true, padding = 8 } = options;

  const [targetElement, setTargetElement] = useState<HTMLElement | null>(null);
  const [label, setLabel] = useState<string | undefined>(undefined);

  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const clear = useCallback(() => {
    setTargetElement(null);
    setLabel(undefined);

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const highlight = useCallback((element: HTMLElement, highlightLabel?: string) => {
    setTargetElement(element);
    setLabel(highlightLabel);
  }, []);

  const highlightBySelector = useCallback(
    (selector: string, highlightLabel?: string): boolean => {
      const element = document.querySelector<HTMLElement>(selector);
      if (element) {
        highlight(element, highlightLabel);
        return true;
      }
      return false;
    },
    [highlight]
  );

  const highlightMultiple = useCallback(
    (elements: HTMLElement[], cycleOptions?: { interval?: number; labels?: string[] }) => {
      if (elements.length === 0) return;

      const interval = cycleOptions?.interval ?? 2000;
      const labels = cycleOptions?.labels ?? [];

      let currentIndex = 0;

      // Show first element immediately
      setTargetElement(elements[0]);
      setLabel(labels[0]);

      // Clear any existing interval
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }

      // Set up cycling interval
      intervalRef.current = setInterval(() => {
        currentIndex = (currentIndex + 1) % elements.length;
        setTargetElement(elements[currentIndex]);
        setLabel(labels[currentIndex]);
      }, interval);
    },
    []
  );

  return {
    targetElement,
    label,
    isHighlighting: targetElement !== null,
    animate,
    padding,
    highlight,
    highlightBySelector,
    clear,
    highlightMultiple,
  };
}

export default useActionHighlight;
